import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Phone, Calendar, ShieldCheck, MapPin, ArrowRight, Star, Clock, Award, Leaf, Smartphone, Bell, Gift, BadgeCheck, Check, Loader2 } from "lucide-react";
import AnimatedCounter from "../components/AnimatedCounter";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import InstallPrompt from "../components/InstallPrompt";
import { api } from "../lib/api";
import { toast } from "sonner";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/2c122ba0-c4f3-4497-9a5a-0002cdd6e57f/images/b5e0a303a3e0a8eae73141adb06484b6bd83f5ad5e327913fe5efa2063ac5c47.png";
const SPARKLE_OVERLAY = "https://static.prod-images.emergentagent.com/jobs/2c122ba0-c4f3-4497-9a5a-0002cdd6e57f/images/4c1845e26a2fcf5b3f9e3f9cca4d1fa8fc996f549630b0ad333e561292a49bc5.png";
const BA_IMAGE = "https://static.prod-images.emergentagent.com/jobs/2c122ba0-c4f3-4497-9a5a-0002cdd6e57f/images/9abc9bb5373474d564639e39250d43aad2f8e9df4b150902e9e9a6c7cb7515f5.png";

const AREAS = ["Atlanta", "Peachtree City", "Fayetteville", "Alpharetta", "Marietta", "Riverdale", "Jonesboro", "McDonough", "Stockbridge"];

const TESTIMONIALS = [
  { name: "Jasmine R.", area: "Buckhead, Atlanta", rating: 5, text: "Booked the deep clean before hosting and my place sparkled. The team was meticulous, on-time, and respectful. Worth every dollar." },
  { name: "Marcus T.", area: "Alpharetta", rating: 5, text: "We use them weekly for our office suite. Reliable, professional, and the AI booking flow saves me time every month." },
  { name: "Priya S.", area: "Peachtree City", rating: 5, text: "Move-out cleaning got us our entire deposit back. They literally restored the place. Highly recommend." },
  { name: "Andre W.", area: "Marietta", rating: 5, text: "Mold remediation in our basement was handled with care. Certified, responsive, and fairly priced." },
];

function NotifyMeForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/leads", {
        name: "App waitlist",
        email,
        message: "Mobile app waitlist signup",
      });
      setDone(true);
      toast.success("You're on the list!", { description: "We'll email you when the app launches." });
    } catch {
      toast.error("Could not save. Try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#0B5D3B]/5 border border-[#0B5D3B]/20 text-[#0B5D3B] text-sm" data-testid="notify-success">
        <Check className="w-4 h-4" /> You're on the waitlist
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="inline-flex items-center gap-2" data-testid="notify-me-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="px-4 py-3 rounded-2xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-sm w-52"
        data-testid="notify-me-email"
      />
      <button
        disabled={loading}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#0B5D3B] text-white text-sm hover:bg-[#063A24] disabled:opacity-60 transition-colors"
        data-testid="notify-me-submit"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Smartphone className="w-4 h-4" /> Notify me</>}
      </button>
    </form>
  );
}

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tIdx, setTIdx] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services || [])).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
    const id = setInterval(() => setTIdx((i) => (i + 1) % TESTIMONIALS.length), 5500);
    return () => clearInterval(id);
  }, []);

  const featured = services.slice(0, 6);
  const allTestimonials = (reviews.length ? reviews.map(r => ({
    name: r.user_name, area: "Verified client", rating: r.rating, text: r.comment
  })) : []).concat(TESTIMONIALS);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#063A24]/95 via-[#0B5D3B]/85 to-[#0B5D3B]/40" />
          <img src={SPARKLE_OVERLAY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 mix-blend-screen pointer-events-none" />
        </div>

        <div className="relative z-10 container-x pt-24 md:pt-32 pb-24 md:pb-40">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-[0.24em] mb-8" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              Metro Atlanta · Premium Cleaning
            </div>
            <h1 className="font-serif text-5xl md:text-7xl text-white leading-[1.05] tracking-tight">
              We Don't Just Clean —<br />
              <span className="gold-shimmer-text italic">We Restore Freshness.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">
              White-glove residential, commercial, automotive, and mold remediation services from a team obsessed with detail.
            </p>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/booking" className="btn-gold" data-testid="hero-book-now">
                <Calendar className="w-4 h-4 mr-2" /> Book Now
              </Link>
              <Link to="/ai-quote" className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-7 py-3.5 rounded-full font-medium hover:bg-white/20 transition-all" data-testid="hero-free-quote">
                Get Free AI Quote <ArrowRight className="w-4 h-4 ml-2 inline" />
              </Link>
              <a href="tel:9432450462" className="bg-transparent text-white border border-white/40 px-7 py-3.5 rounded-full font-medium hover:bg-white hover:text-[#0B5D3B] transition-all inline-flex items-center" data-testid="hero-call-now">
                <Phone className="w-4 h-4 mr-2" /> 943-245-0462
              </a>
            </div>

            {/* trust strip */}
            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-white/70 text-xs uppercase tracking-[0.18em]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Insured & Bonded</div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#D4AF37]" /> 5★ Google Reviews</div>
              <div className="flex items-center gap-2"><Leaf className="w-4 h-4 text-[#D4AF37]" /> Eco-friendly</div>
            </div>
          </motion.div>
        </div>

        {/* Stats bar */}
        <div className="relative z-10 -mb-16">
          <div className="container-x">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#EAE3D2] rounded-2xl overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.15)]" data-testid="stats-bar">
              {[
                { v: 12480, suffix: "+", label: "Homes Cleaned" },
                { v: 3200, suffix: "+", label: "Happy Clients" },
                { v: 12, suffix: "yrs", label: "Experience" },
                { v: 98, suffix: "%", label: "Retention Rate" },
              ].map((s, i) => (
                <div key={i} className="bg-white p-8 text-center">
                  <div className="font-serif text-4xl md:text-5xl text-[#0B5D3B] font-semibold" data-testid={`stat-${i}`}>
                    <AnimatedCounter to={s.v} suffix={s.suffix} />
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[#4A554E]">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE/AFTER */}
      <section className="container-x py-32 md:py-40 mt-12" data-testid="before-after-section">
        <div className="max-w-3xl mb-12">
          <div className="overline mb-4">The Difference</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111]">
            Drag to see the <span className="gold-shimmer-text">transformation.</span>
          </h2>
          <p className="mt-4 text-[#4A554E] text-lg">From overlooked corners to gleaming surfaces — every detail is handled.</p>
        </div>
        <BeforeAfterSlider image={BA_IMAGE} />
      </section>

      {/* FEATURED SERVICES */}
      <section className="bg-[#FAF7EE]" data-testid="featured-services-section">
        <div className="container-x py-32 md:py-40">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="overline mb-4">Featured Services</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111]">A service tier for every space.</h2>
            </div>
            <Link to="/services" className="text-[#0B5D3B] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
              View all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((s, i) => (
              <motion.div key={s.id}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="card-luxe overflow-hidden group" data-testid={`service-card-${s.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="font-serif text-2xl text-[#111]">{s.name}</h3>
                    <div className="text-[#D4AF37] font-semibold text-sm">${s.price_from}+</div>
                  </div>
                  <p className="text-sm text-[#4A554E] leading-relaxed mb-5">{s.description}</p>
                  <div className="flex items-center justify-between text-xs text-[#7A8A81]">
                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {s.duration}</span>
                    <Link to={`/booking?service=${s.id}`} className="text-[#0B5D3B] font-medium hover:gap-2 inline-flex items-center gap-1 transition-all" data-testid={`book-${s.id}`}>
                      Book <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMO BANNER (Facebook cover) */}
      <section className="container-x pt-32 md:pt-40" data-testid="promo-banner-section">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <div className="overline mb-4">First-Time Offer</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111] leading-tight">
              Save <span className="gold-shimmer-text">10% off</span> your first service.
            </h2>
            <p className="mt-5 text-[#4A554E] text-lg max-w-md">
              Fully insured. Satisfaction guaranteed. 24/7 service. New clients save 10% on residential, deep, mold, car detailing, move-in/out, and Airbnb cleaning.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/booking" className="btn-primary" data-testid="promo-book-cta">Claim 10% Off</Link>
              <a href="tel:9432450462" className="btn-secondary" data-testid="promo-call-cta">
                <Phone className="w-4 h-4 mr-2" /> Call or Text
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.2em] text-[#7A8A81]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0B5D3B]" /> Fully Insured</div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#D4AF37]" /> Satisfaction Guaranteed</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#0B5D3B]" /> 24/7 Service</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(11,93,59,0.18)] border border-[#EAE3D2]">
              <img
                src="https://customer-assets.emergentagent.com/job_premium-clean-atl/artifacts/i2w4gsp2_Facebook%20Cover%20page.png"
                alt="ShoNics Kleaning Solutions services overview"
                className="w-full h-auto block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-x py-32 md:py-40" data-testid="testimonials-section">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-4">What Clients Say</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111]">Trusted by 3,000+ homes & businesses.</h2>
        </div>
        <div className="relative">
          <div className="card-luxe p-10 md:p-14 max-w-3xl mx-auto text-center min-h-[280px] flex flex-col items-center justify-center sparkle relative overflow-hidden">
            <div className="flex gap-1 mb-6">
              {[...Array(allTestimonials[tIdx]?.rating || 5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>
            <p className="font-serif text-xl md:text-2xl leading-relaxed text-[#111] italic">
              "{allTestimonials[tIdx]?.text}"
            </p>
            <div className="mt-6 text-sm">
              <div className="font-semibold text-[#0B5D3B]">{allTestimonials[tIdx]?.name}</div>
              <div className="text-[#7A8A81]">{allTestimonials[tIdx]?.area}</div>
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-8">
            {allTestimonials.map((_, i) => (
              <button key={i} onClick={() => setTIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === tIdx ? "w-10 bg-[#0B5D3B]" : "w-3 bg-[#EAE3D2]"}`}
                aria-label={`Testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="bg-[#111111] text-white relative overflow-hidden" data-testid="service-areas-section">
        <img src={SPARKLE_OVERLAY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none" />
        <div className="relative container-x py-32 md:py-40 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline text-[#D4AF37] mb-4">Service Areas</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
              Now serving <span className="gold-shimmer-text">8 metro Atlanta cities.</span>
            </h2>
            <p className="mt-5 text-white/70 text-lg max-w-md">From downtown Atlanta to Stockbridge — same-day availability and recurring contracts across the metro.</p>
            <Link to="/booking" className="btn-gold mt-8" data-testid="areas-cta">Schedule a clean</Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {AREAS.map((a) => (
              <div key={a} className="flex items-center gap-3 px-5 py-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-md hover:bg-white/10 transition-all" data-testid={`area-${a.toLowerCase().replace(" ","-")}`}>
                <MapPin className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-serif text-lg">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APP DOWNLOAD */}
      <section className="container-x py-32 md:py-40" data-testid="app-download-section">
        <div className="card-luxe overflow-hidden grid md:grid-cols-12 gap-0 bg-white">
          {/* Left content */}
          <div className="md:col-span-7 p-10 md:p-16 flex flex-col justify-center">
            <div className="overline mb-4">Mobile App</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111] leading-tight">
              The ShoNics app — <span className="gold-shimmer-text">freshness in your pocket.</span>
            </h2>
            <p className="mt-5 text-[#4A554E] text-lg max-w-lg">
              Book in seconds, track your cleaner in real-time, earn loyalty points, and unlock referral rewards. Coming soon to iOS and Android.
            </p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-lg">
              <div className="flex items-start gap-2.5">
                <Bell className="w-4 h-4 text-[#0B5D3B] mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-[#111]">Live tracking</div>
                  <div className="text-xs text-[#7A8A81]">Watch your cleaner arrive</div>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Gift className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-[#111]">Loyalty perks</div>
                  <div className="text-xs text-[#7A8A81]">Earn on every clean</div>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <BadgeCheck className="w-4 h-4 text-[#0B5D3B] mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-[#111]">1-tap booking</div>
                  <div className="text-xs text-[#7A8A81]">Same-day in select areas</div>
                </div>
              </div>
            </div>

            <div className="mt-9 flex flex-wrap gap-3" data-testid="app-store-buttons">
              <InstallPrompt />
              <NotifyMeForm />
            </div>
          </div>

          {/* Right phone mockup */}
          <div className="md:col-span-5 relative bg-gradient-to-br from-[#063A24] via-[#0B5D3B] to-[#063A24] p-10 md:p-12 overflow-hidden flex items-center justify-center min-h-[420px]">
            <img src={SPARKLE_OVERLAY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
            {/* Phone */}
            <div className="relative w-[260px] h-[520px] bg-[#111] rounded-[44px] border-[10px] border-[#1d1d1d] shadow-[0_30px_80px_rgba(0,0,0,0.4)] overflow-hidden">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#1d1d1d] rounded-b-2xl z-10" />
              {/* Screen */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#FAF7EE] to-white p-5 pt-10 flex flex-col">
                <div className="flex items-center gap-2 mb-5">
                  <img src="https://customer-assets.emergentagent.com/job_premium-clean-atl/artifacts/x1iqqas7_ShoNics%20Logo.jpg" alt="" className="w-9 h-9 rounded-lg object-contain" />
                  <div>
                    <div className="font-serif text-base leading-none text-[#0B5D3B]">ShoNics</div>
                    <div className="text-[8px] uppercase tracking-[0.18em] text-[#D4AF37]">Kleaning</div>
                  </div>
                </div>
                <div className="rounded-2xl bg-[#0B5D3B] p-4 mb-3 text-white">
                  <div className="text-[9px] uppercase tracking-widest text-white/60">Next clean</div>
                  <div className="font-serif text-xl mt-1">Sat · 10:00 AM</div>
                  <div className="text-[10px] text-white/70 mt-0.5">Residential · 2 BR</div>
                </div>
                <div className="rounded-2xl border border-[#EAE3D2] p-3 mb-3 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#A88926]">
                    <Sparkles className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-[#111]">AI Quote ready</div>
                    <div className="text-[9px] text-[#7A8A81]">$180 – $260 estimate</div>
                  </div>
                </div>
                <div className="rounded-2xl border border-[#EAE3D2] p-3 mb-3">
                  <div className="text-[9px] uppercase tracking-widest text-[#7A8A81]">Loyalty</div>
                  <div className="flex items-baseline gap-1 mt-0.5">
                    <span className="font-serif text-2xl text-[#0B5D3B]">820</span>
                    <span className="text-[10px] text-[#7A8A81]">/ 1,000 pts</span>
                  </div>
                  <div className="h-1.5 bg-[#EAE3D2] rounded-full mt-1.5 overflow-hidden">
                    <div className="h-full bg-[#D4AF37] w-[82%]" />
                  </div>
                </div>
                <div className="mt-auto rounded-full bg-[#0B5D3B] text-white text-center text-[11px] font-semibold py-2.5">
                  Book a clean
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-24" data-testid="bottom-cta">
        <div className="card-luxe p-12 md:p-16 text-center bg-gradient-to-br from-[#063A24] via-[#0B5D3B] to-[#063A24] text-white relative overflow-hidden">
          <img src={SPARKLE_OVERLAY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
          <div className="relative">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Ready to restore your space?</h2>
            <p className="mt-4 text-white/80 max-w-xl mx-auto">Book in under 60 seconds. Same-day availability in select areas.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/booking" className="btn-gold">Book Now</Link>
              <Link to="/ai-quote" className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-7 py-3.5 rounded-full font-medium hover:bg-white/20 transition-all">Try AI Quote</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
